import React from "react"
import { Row, Card, CardBody, Col } from "reactstrap"
import { ValidationBadge } from "./ReportValidation"

// Shared brand-consistent building blocks for all printable / PDF reports.
// Keeping these in one place ensures every report header, score badge and
// overall-score chip looks identical across Daily Monitoring, Monthly
// Monitoring, Statutory, Health & Safety and Daily Work reports.

export const NAVY = "#1D3A5F"
export const TEAL = "#2EA99B"

const exact = {
  WebkitPrintColorAdjust: "exact",
} as const

// Normalise a stored yes/no/na value for display: capitalise first letter,
// "n/a"/"na" -> "NA". e.g. "yes" -> "Yes", "no" -> "No", "na" -> "NA".
export const fmtYNA = (raw: any) => {
  const v = (raw == null ? "" : String(raw)).trim()
  if (!v) return ""
  const l = v.toLowerCase()
  if (l === "yes") return "Yes"
  if (l === "no") return "No"
  if (l === "na" || l === "n/a") return "NA"
  return v.charAt(0).toUpperCase() + v.slice(1)
}

// green / amber / red palette for a compliance score
export const scoreColor = (v: any) => {
  const n = Math.round(Number(v) || 0)
  if (n >= 80) return { bg: "#e5f6f2", fg: "#1c8577", bd: "#2ea99b" }
  if (n >= 50) return { bg: "#fdf3e3", fg: "#b7791f", bd: "#e0a83c" }
  return { bg: "#fdecec", fg: "#c0392b", bd: "#e07a6f" }
}

// small pill used inside score cells of a report table
export const ScoreBadge = ({ value }: { value: any }) => {
  const c = scoreColor(value)
  const n = Math.round(Number(value) || 0)
  return (
    <span
      style={{
        display: "inline-block",
        minWidth: "34px",
        textAlign: "center",
        padding: "2px 10px",
        borderRadius: "12px",
        fontWeight: 700,
        fontSize: "13px",
        color: c.fg,
        backgroundColor: c.bg,
        border: `1px solid ${c.bd}`,
        ...exact,
      }}
    >
      {n}
    </span>
  )
}

// coloured pill for a yes / no / n-a status value
export const StatusBadge = ({ value }: { value: any }) => {
  const raw = (value == null ? "" : String(value)).trim()
  if (!raw) return <span />
  const v = raw.toLowerCase()
  let c = { bg: "#eef1f4", fg: "#555", bd: "#cdd5dd" }
  if (v === "yes") c = { bg: "#e5f6f2", fg: "#1c8577", bd: "#2ea99b" }
  else if (v === "no") c = { bg: "#fdecec", fg: "#c0392b", bd: "#e07a6f" }
  else if (v === "n/a" || v === "na") c = { bg: "#fdf3e3", fg: "#b7791f", bd: "#e0a83c" }
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "12px",
        color: c.fg,
        backgroundColor: c.bg,
        border: `1px solid ${c.bd}`,
        ...exact,
      }}
    >
      {fmtYNA(raw)}
    </span>
  )
}

// large "Overall Score" chip shown at the top-right of a report
export const OverallScore = ({ value }: { value: any }) => {
  const c = scoreColor(value)
  return (
    <div
      style={{
        textAlign: "center",
        padding: "10px 22px",
        borderRadius: "10px",
        backgroundColor: c.bg,
        border: `2px solid ${c.bd}`,
        ...exact,
      }}
    >
      <div
        style={{
          fontSize: "11px",
          letterSpacing: "1px",
          textTransform: "uppercase",
          color: c.fg,
          fontWeight: 600,
        }}
      >
        Overall Score
      </div>
      <div style={{ fontSize: "30px", fontWeight: 800, lineHeight: 1.1, color: c.fg }}>
        {Math.round(Number(value) || 0)}
      </div>
    </div>
  )
}

interface HeaderProps {
  reportName?: string
  factoryName?: string
  submitedBy?: string
  date?: string
  overallscore?: any
  showScore?: boolean
  validation?: any
}

const Field = ({ label, value, bold }: { label: string; value: any; bold?: boolean }) => (
  <span style={{ display: "flex", marginTop: "4px" }}>
    <h5 style={{ margin: 0, color: "#556", fontSize: "14px" }}>{label} :</h5>{" "}
    <p
      style={{
        padding: "0 4px",
        margin: 0,
        fontWeight: bold ? 700 : 400,
        color: bold ? NAVY : "#222",
      }}
      className="ml-2 mr-1"
    >
      {value}
    </p>
  </span>
)

// The standard report header card: title on the left, factory / submitter /
// date underneath, and an optional Overall Score chip on the right.
export const PrintHeader = (props: HeaderProps) => {
  const { reportName, factoryName, submitedBy, date, overallscore, showScore = true, validation } = props
  return (
    <Row>
      <Col lg={12}>
        <Card style={{ marginBottom: "10px" }}>
          <CardBody style={{ padding: "12px 20px" }}>
            {/* Content-hugging flex row (not a Bootstrap grid) so the card is
                exactly as tall as its content: the Overall Score sits at the
                top-right and there is no empty space left below it. */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                {reportName && (
                  <h4 style={{ margin: 0, color: NAVY }}>
                    {reportName}
                    {validation && validation.status && (
                      <span style={{ marginLeft: 10, verticalAlign: "middle" }}>
                        <ValidationBadge validation={validation} />
                      </span>
                    )}
                  </h4>
                )}
                <div style={{ marginTop: "8px" }}>
                  <Field label="Factory Name" value={factoryName} bold />
                  {submitedBy != null && submitedBy !== "" && (
                    <Field label="Submit By" value={submitedBy} />
                  )}
                  {date && <Field label="Date" value={date} />}
                </div>
              </div>
              {showScore && (
                <div style={{ flexShrink: 0 }}>
                  <OverallScore value={overallscore} />
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}
